<template>
  <v-app class="admin-layout" :class="{ 'is-console': isConsole, 'nav-docked': navPermanent, 'nav-collapsed': navCollapsed }">
    <!-- 상단 헤더 -->
    <header class="app-header">
      <div class="header-left">
        <v-btn
          v-if="showNavToggle"
          icon
          variant="text"
          color="white"
          size="small"
          class="menu-toggle-btn"
          :aria-label="navToggleLabel"
          @click="toggleNav"
        >
          <v-icon>{{ navToggleIcon }}</v-icon>
        </v-btn>
        <button type="button" class="app-brand app-brand-btn" @click="goHome">
          <span class="app-title">CAION</span>
          <span class="app-tagline">장기요양 통합관리</span>
        </button>
      </div>
      <div class="header-right">
        <!-- 대리 로그인 표시 -->
        <div v-if="isImpersonating" class="impersonate-indicator" @click="exitImpersonation">
          <v-icon size="18" class="impersonate-icon">mdi-incognito</v-icon>
          <div class="impersonate-info">
            <span class="from-user">{{ originalUser?.name }}</span>
            <span class="arrow">→</span>
            <span class="to-user">{{ currentUser?.name }}</span>
          </div>
          <v-icon size="16" class="exit-icon">mdi-close-circle</v-icon>
        </div>

        <!-- 사업구분 전환 (다중 사업구분인 경우) -->
        <div
          v-if="!isImpersonating && hasMultipleBizGubun"
          class="biz-gubun-switcher"
          @click="openBizGubunDialog"
        >
          <div class="biz-color-dot" :style="{ backgroundColor: bizGubunColor }"></div>
          <div class="biz-info">
            <span class="org-name">{{ currentUser?.name }}</span>
            <span class="biz-name">{{ bizGubunName }}</span>
          </div>
          <v-icon size="18" color="white" class="switch-icon">mdi-swap-horizontal</v-icon>
        </div>

        <!-- 유저 정보 (단일 사업구분) -->
        <div class="user-info" v-if="!isImpersonating && !hasMultipleBizGubun && currentUser">
          <span class="header-role-badge">{{ currentUser.roleName }}</span>
          <span class="user-name">{{ currentUser.name }}님</span>
        </div>

        <!-- 계정 메뉴 -->
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn icon size="small" variant="text" color="white" v-bind="props">
              <v-icon>mdi-account-circle</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <template v-if="isImpersonating">
              <v-list-item class="impersonate-menu-item">
                <div class="impersonate-menu-info">
                  <v-icon size="small" color="deep-purple">mdi-incognito</v-icon>
                  <div class="info-text">
                    <span class="label">접속 중인 계정</span>
                    <span class="value">{{ currentUser?.name }}</span>
                  </div>
                </div>
              </v-list-item>
              <v-list-item @click="exitImpersonation" class="exit-menu-item">
                <template v-slot:prepend>
                  <v-icon size="small" color="error">mdi-arrow-left-circle</v-icon>
                </template>
                <v-list-item-title class="text-error">{{ originalUser?.name }}(으)로 복귀</v-list-item-title>
              </v-list-item>
              <v-divider />
            </template>
            <v-list-item v-if="isOnMemberPages" @click="goToMyInfo">
              <template v-slot:prepend>
                <v-icon size="small">mdi-information-outline</v-icon>
              </template>
              <v-list-item-title>기관 정보</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!isImpersonating && !isOnMemberPages" @click="profileDialog = true">
              <template v-slot:prepend>
                <v-icon size="small">mdi-account-cog-outline</v-icon>
              </template>
              <v-list-item-title>내 정보</v-list-item-title>
            </v-list-item>
            <v-divider v-if="isOnMemberPages || (!isImpersonating && !isOnMemberPages)" />
            <v-list-item @click="handleLogout">
              <template v-slot:prepend>
                <v-icon size="small">mdi-logout</v-icon>
              </template>
              <v-list-item-title>로그아웃</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </header>

    <!-- 네비게이션 드로어 -->
    <AdminNavi
      v-model="drawer"
      :permanent="navPermanent"
      :collapsed="navCollapsed"
      :menu-items="filteredMenuItems"
      :admin-items="adminMenuItems"
      :supervisor-items="supervisorMenuItems"
    />

    <!-- 메인 콘텐츠 -->
    <div class="admin-main">
      <slot />
    </div>

    <UserProfileDialog v-model="profileDialog" />

    <!-- 복귀 확인 다이얼로그 -->
    <v-dialog v-model="exitDialog" max-width="380px">
      <v-card class="exit-dialog">
        <v-card-title class="exit-header">
          <span class="exit-header-title">
            <v-icon class="mr-2" color="warning">mdi-arrow-left-circle</v-icon>
            권한 복귀
          </span>
          <button type="button" class="exit-close" aria-label="닫기" @click="exitDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="exit-body">
          <p><strong>{{ originalUser?.name }}</strong> 계정으로 돌아가시겠습니까?</p>
        </v-card-text>
        <v-card-actions class="exit-actions">
          <v-spacer />
          <v-btn variant="text" class="btn-cancel" @click="exitDialog = false">취소</v-btn>
          <v-btn variant="flat" class="btn-confirm" @click="confirmExit">복귀하기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 사업구분 전환 다이얼로그 -->
    <v-dialog v-model="bizGubunDialog" max-width="420px">
      <v-card class="biz-gubun-dialog">
        <v-card-title class="biz-dialog-header">
          <span class="biz-dialog-header-title">
            <v-icon class="mr-2">mdi-swap-horizontal-circle</v-icon>
            사업구분 전환
          </span>
          <button
            type="button"
            class="biz-dialog-close"
            aria-label="닫기"
            :disabled="switching"
            @click="bizGubunDialog = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="biz-dialog-body">
          <p class="biz-dialog-desc">다른 사업구분으로 전환합니다.</p>
          <div class="biz-gubun-list" v-if="!loadingBizGubun">
            <div
              v-for="member in bizGubunList"
              :key="member.idx"
              class="biz-gubun-item"
              :class="{
                current: member.idx === currentUser?.idx,
                selected: selectedBizGubunIdx === member.idx
              }"
              @click="selectBizGubun(member)"
            >
              <div class="biz-color" :style="{ backgroundColor: member.bizGubunColor }"></div>
              <div class="biz-info">
                <span class="member-name">{{ member.name }}</span>
                <span class="member-biz">{{ member.bizGubunName || member.bizGubun }}</span>
              </div>
              <v-chip v-if="member.idx === currentUser?.idx" size="x-small" color="primary" variant="flat">현재</v-chip>
              <v-icon v-else-if="selectedBizGubunIdx === member.idx" color="primary">mdi-check-circle</v-icon>
            </div>
          </div>
          <div v-else class="loading-area">
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>
        </v-card-text>
        <v-card-actions class="biz-dialog-actions">
          <v-btn variant="text" class="btn-cancel" @click="bizGubunDialog = false">취소</v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            class="btn-confirm"
            :disabled="!selectedBizGubunIdx || selectedBizGubunIdx === currentUser?.idx"
            :loading="switching"
            @click="confirmBizGubunSwitch"
          >
            전환하기
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import AdminNavi from './AdminNavi.vue';
import UserProfileDialog from '@/components/common/UserProfileDialog.vue';
import { ADMIN_NAVIGATION_ITEMS, MEMBER_NAVIGATION_ITEMS, type NavItem } from '@/config/navigation';

const store = useStore();
const router = useRouter();
const route = useRoute();

const NAV_COLLAPSE_KEY = 'caion_nav_collapsed';
const drawer = ref(false);
const isWide = ref(typeof window !== 'undefined' ? window.innerWidth >= 1180 : true);
const navCollapsedPref = ref(
  typeof window !== 'undefined' && localStorage.getItem(NAV_COLLAPSE_KEY) === '1'
);
const exitDialog = ref(false);
const profileDialog = ref(false);

// 사업구분 관련 상태
const bizGubunDialog = ref(false);
const bizGubunList = ref<any[]>([]);
const selectedBizGubunIdx = ref<number | null>(null);
const loadingBizGubun = ref(false);
const switching = ref(false);

const currentUser = computed(() => store.getters.currentUser);
const isImpersonating = computed(() => store.getters.isImpersonating);
const originalUser = computed(() => store.getters.originalUser);
const hasMultipleBizGubun = computed(() => store.getters.hasMultipleBizGubun);
const bizGubunName = computed(() => store.getters.bizGubunName);
const bizGubunColor = computed(() => store.getters.bizGubunColor);
const roleLevel = computed(() => Number(store.getters.roleLevel || 0));

// 멤버 페이지(/m/*)인지 관리 페이지인지 판단
const isOnMemberPages = computed(() => route.path.startsWith('/m/'));
const isConsole = computed(() => !isOnMemberPages.value);
const navPermanent = computed(() => isConsole.value && isWide.value);
const navCollapsed = computed(() => navPermanent.value && navCollapsedPref.value);
const showNavToggle = computed(() => isConsole.value || !navPermanent.value);
const navToggleLabel = computed(() => {
  if (!navPermanent.value) return drawer.value ? '메뉴 닫기' : '메뉴 열기';
  return navCollapsed.value ? '메뉴 펼치기' : '메뉴 줄이기';
});
const navToggleIcon = computed(() => {
  if (!navPermanent.value) return drawer.value ? 'mdi-close' : 'mdi-menu';
  return navCollapsed.value ? 'mdi-menu' : 'mdi-menu-open';
});

const toggleNav = () => {
  if (navPermanent.value) {
    navCollapsedPref.value = !navCollapsedPref.value;
    localStorage.setItem(NAV_COLLAPSE_KEY, navCollapsedPref.value ? '1' : '0');
    return;
  }
  drawer.value = !drawer.value;
};

const syncDrawer = () => {
  if (navPermanent.value) drawer.value = true;
};

const onResize = () => {
  isWide.value = window.innerWidth >= 1180;
};

watch(navPermanent, syncDrawer);
onMounted(() => {
  window.addEventListener('resize', onResize);
  syncDrawer();
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});

// 라우트 컨텍스트에 따라 네비게이션 항목 동적 선택
const activeNavItems = computed(() => {
  return isOnMemberPages.value ? MEMBER_NAVIGATION_ITEMS : ADMIN_NAVIGATION_ITEMS;
});

const filterMenuByRole = (items: NavItem[]): NavItem[] => {
  return items
    .filter((item) => {
      const minRole = item.minRole ?? 0;
      return roleLevel.value >= minRole;
    })
    .map((item) => {
      if (!item.children) return item;
      const children = filterMenuByRole(item.children);
      if (children.length === 0) return { ...item, children: [] };
      return { ...item, children };
    })
    .filter((item) => !item.children || item.children.length > 0 || !!item.to);
};

const roleFilteredNav = computed(() => filterMenuByRole(activeNavItems.value));

const filteredMenuItems = computed(() => {
  if (isOnMemberPages.value) return roleFilteredNav.value;
  return roleFilteredNav.value.filter((item) => (item.minRole ?? 0) < 80);
});

// 관리자(80~99) / 슈퍼바이저(100) — 멤버 페이지에서는 묶지 않음
const adminMenuItems = computed(() => {
  if (isOnMemberPages.value) return [];
  return roleFilteredNav.value.filter((item) => {
    const min = item.minRole ?? 0;
    return min >= 80 && min < 100;
  });
});

const supervisorMenuItems = computed(() => {
  if (isOnMemberPages.value) return [];
  return roleFilteredNav.value.filter((item) => (item.minRole ?? 0) >= 100);
});

const goHome = () => {
  const target = isOnMemberPages.value ? '/m/info' : '/dashboard';
  if (route.path !== target) router.push(target);
};

const goToMyInfo = () => {
  router.push('/m/myinfo');
};

const handleLogout = () => {
  store.dispatch('logout');
  window.location.href = '/login';
};

const exitImpersonation = () => {
  exitDialog.value = true;
};

const confirmExit = async () => {
  await store.dispatch('exitImpersonation');
  window.location.reload();
};

// 사업구분 다이얼로그 열기
const openBizGubunDialog = async () => {
  bizGubunDialog.value = true;
  loadingBizGubun.value = true;
  selectedBizGubunIdx.value = null;

  try {
    const result = await store.dispatch('fetchBizGubunList');
    if (result.success) {
      bizGubunList.value = result.data;
    }
  } catch (error) {
    console.error('사업구분 목록 조회 실패:', error);
  } finally {
    loadingBizGubun.value = false;
  }
};

// 사업구분 선택
const selectBizGubun = (member: any) => {
  if (member.idx !== currentUser.value?.idx) {
    selectedBizGubunIdx.value = member.idx;
  }
};

// 사업구분 전환 확인
const confirmBizGubunSwitch = async () => {
  if (!selectedBizGubunIdx.value) return;

  switching.value = true;

  try {
    const result = await store.dispatch('switchBizGubun', selectedBizGubunIdx.value);
    if (result.success) {
      bizGubunDialog.value = false;
      window.location.reload();
    }
  } finally {
    switching.value = false;
  }
};
</script>
